import React, { useCallback, useEffect, useMemo } from 'react';

import {
  AccountManager,
  btag,
  DateService,
  pushToDataLayer,
  ResponsibleApi,
  SiteConfigManager,
} from '@atlas/core';
import { HeaderTitle, Label } from '@atlas/ui';

import useSelfExclusion from 'components/ClientProfile/ResponsibleGaming/SelfExclusion/useSelfExclusion';

import SelfExclusionChips from './SelfExclusionChips';
import SelfExclusionConfirmPopup from './SelfExclusionConfirmPopup';

const SelfExclusion = () => {
  const {
    isLoading = true,
    isPopupOpen,
    onCloseSubmitPopup,
    onOpenSubmitPopup,
    selectedPeriod,
    selfExcludedDueDate,
    setSelectedPeriod,
    setState,
  } = useSelfExclusion();

  useEffect(() => {
    setState({ isLoading: true });
    try {
      (async () => {
        const { period, selfExcludedDueDate: date } =
          await ResponsibleApi.getSelfExclusionSettings();

        setState({
          isLoading: false,
          selectedPeriod: period,
          selfExcludedDueDate: date,
        });
      })();
    } catch (e) {
      console.error(e);
      setState({ isLoading: false });
    }
  }, []);

  const onSubmit = useCallback(async () => {
    if (isLoading) return;
    setState({ isLoading: true });
    try {
      const { days, selfExcludedDueDate: date } = await ResponsibleApi.setSelfExclusionSettingsV2({
        days: selectedPeriod,
      });

      pushToDataLayer('self_exclusion', { date, days }, { userInfo: AccountManager.account }, btag);

      setState({
        isLoading: false,
        isPopupOpen: false,
        selectedPeriod: days,
        selfExcludedDueDate: date,
      });
      AccountManager.logout();
    } catch (e) {
      console.error(e);
      setState({
        isLoading: false,
        isPopupOpen: false,
      });
    }
  }, [selectedPeriod, isLoading]);

  const selfExclusionsOptions = useMemo(() => {
    const selfExclusionsConfig = SiteConfigManager.getConfig('config.config.selfExclusions', []);
    const selfExclusionsTimeRanges = selfExclusionsConfig
      .filter(selfExclusion => 'timeRange' in selfExclusion && selfExclusion.enabled)
      .sort((a, b) => a.timeRange - b.timeRange)
      .map(({ timeRange }) => ({
        text: `${timeRange} day${timeRange > 1 ? 's' : ''}`,
        value: timeRange,
      }));
    const selfExclusionsForever = selfExclusionsConfig
      .filter(selfExclusion => 'forever' in selfExclusion && selfExclusion.enabled)
      .map(() => ({
        text: 'forever',
        value: 0,
      }));

    return selfExclusionsTimeRanges.concat(selfExclusionsForever);
  }, []);

  const warningMessage = useMemo(() => {
    if (!selfExcludedDueDate) return;

    return (
      <>
        <div className="title-wrapper">
          <HeaderTitle className="sub-text block-medium" title="you_are_already_self_excluded" />
        </div>
        <div className="description">
          <div className="info-list-container">
            {selfExcludedDueDate && (
              <div className="info-list-item">
                <Label className="sub-title-text" message="self_exclude_time" />
                <span className="title-small-text">
                  {DateService.getFormattedDateWithTime(selfExcludedDueDate)}
                </span>
              </div>
            )}
            {typeof selectedPeriod === 'number' && (
              <div className="info-list-item">
                <Label className="sub-title-text" message="self_exclude_period" />
                <span className="title-small-text">{selectedPeriod}</span>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }, [selfExcludedDueDate]);

  const chips = useMemo(() => {
    if (selfExcludedDueDate) return;

    return (
      <SelfExclusionChips
        isLoading={isLoading}
        onOpenSubmitPopup={onOpenSubmitPopup}
        selectedPeriod={selectedPeriod}
        selfExclusionsOptions={selfExclusionsOptions}
        setSelectedPeriod={setSelectedPeriod}
      />
    );
  }, [
    selfExcludedDueDate,
    isLoading,
    onOpenSubmitPopup,
    selectedPeriod,
    selfExclusionsOptions,
    setSelectedPeriod,
  ]);

  return (
    <>
      <div className="self-exclusion-container">
        {!isLoading && (
          <>
            <div className="title-wrapper">
              <HeaderTitle className="sub-text block-medium" title="about_self_exclusion" />
            </div>
            <Label className="title-small-300-text description" message="self_exclusion_message" />
            {warningMessage}
            {chips}
          </>
        )}
      </div>
      {isPopupOpen && (
        <SelfExclusionConfirmPopup
          onClose={onCloseSubmitPopup}
          onSubmit={onSubmit}
          selectedPeriod={selectedPeriod}
          selfExclusionsOptions={selfExclusionsOptions}
        />
      )}
    </>
  );
};

export default SelfExclusion;
